import useRewardSettings from "@/hooks/data/useRewardSettings";
import type { SupSingleVaultV3, SupStoredFavToken, Networkish, SupStoredFilterRangeValue } from "@/types";
import { useMemo } from "react";

import { useStoredData } from "@/store/Context";
import useVaults from "@/hooks/api/useVaults";
import { getNetVaultApy, getProtocolName, getSortingDate, getVaultApy } from "@/utils";
import { isAuthenticated } from "@/utils/vault";

/**
 * Fetch and filter vaults based on the global filters & local filterVaults param
 */
export default function useFilterVaults(
  filterVaults: string[] | SupSingleVaultV3[] = [],
  forceReload = false,
  allowFavoritesFilter = true,
  processVisibility = true,
  processAuthenticatedFilter = true,
  enabled = true
) {
  const { data: apiRawVaults, isLoading, isError } = useVaults(forceReload ? "on-mount" : "cached", enabled);
  const storedData = useStoredData();

  const rewardSettings = useRewardSettings();

  const isCompleted = useMemo(() => {
    return apiRawVaults !== undefined && apiRawVaults.length > 0;
  }, [apiRawVaults]);

  const rawVaults = useMemo(() => {
    if (!enabled) {
      return [];
    }
    if (filterVaults.length > 0) {
      const filteredIds = filterVaults.map(singleVault => (typeof singleVault === "string" ? singleVault : singleVault.id));
      return apiRawVaults ? apiRawVaults.filter(singleVault => filteredIds.includes(singleVault.id)) : [];
    }
    return apiRawVaults || [];
  }, [apiRawVaults, filterVaults, enabled]);

  const vaults = useMemo(() => {
    if (!rawVaults.length) {
      return [];
    }
    let vaults = rawVaults;

    let filteredVaults = vaults.length > 0 ? [...vaults] : ([] as SupSingleVaultV3[]);
    storedData.filters.forEach(singleFilter => {
      switch (singleFilter.name) {
        case "chain":
          if (Array.isArray(singleFilter.values) && singleFilter.values.length > 0) {
            filteredVaults = filteredVaults.filter(vault => (singleFilter.values as Networkish[]).includes(vault.chain.id)) as SupSingleVaultV3[];
          }
          break;
        case "vaults_with_rewards":
          if (singleFilter.values as boolean) {
            filteredVaults = filteredVaults.filter(vault => (vault.rewards ?? []).length > 0) as SupSingleVaultV3[];
          }
          break;
        case "protocol":
          if (Array.isArray(singleFilter.values) && singleFilter.values.length > 0) {
            filteredVaults = filteredVaults.filter(vault => (singleFilter.values as string[]).includes(getProtocolName(vault))) as SupSingleVaultV3[];
          }
          break;
        case "tvl":
          if (singleFilter.values) {
            const rangeValue = singleFilter.values as SupStoredFilterRangeValue;
            filteredVaults = filteredVaults.filter(
              vault => vault.vault_statistics.displayTvl >= rangeValue.min && vault.vault_statistics.displayTvl <= rangeValue.max
            ) as SupSingleVaultV3[];
          }
          break;
        case "apy":
          if (singleFilter.values) {
            const rangeValue = singleFilter.values as SupStoredFilterRangeValue;
            filteredVaults = filteredVaults.filter(
              vault => getNetVaultApy(vault, rewardSettings) >= rangeValue.min && getNetVaultApy(vault, rewardSettings) <= rangeValue.max
            ) as SupSingleVaultV3[];
          }
          break;
        case "sharpe":
          if (singleFilter.values) {
            const rangeValue = singleFilter.values as SupStoredFilterRangeValue;
            filteredVaults = filteredVaults.filter(
              vault => vault.vault_statistics.displaySharpe >= rangeValue.min && vault.vault_statistics.displaySharpe <= rangeValue.max
            ) as SupSingleVaultV3[];
          }
          break;
        case "age":
          if (singleFilter.values) {
            const rangeValue = singleFilter.values as SupStoredFilterRangeValue;
            filteredVaults = filteredVaults.filter(
              vault => parseInt(getSortingDate(vault.created_at)) >= rangeValue.min && parseInt(getSortingDate(vault.created_at)) <= rangeValue.max
            ) as SupSingleVaultV3[];
          }
          break;
        case "yield_type":
          if (singleFilter.values) {
            const selectedValues = singleFilter.values as string[];
            if (selectedValues.length > 0) {
              filteredVaults = filteredVaults.filter(vault => selectedValues.includes(vault.yield_type ?? "")) as SupSingleVaultV3[];
            }
          }
          break;
        case "token":
          // eslint-disable-next-line no-case-declarations
          const values = singleFilter.values as SupStoredFavToken[];
          if (Array.isArray(values) && values.length > 0) {
            const filter = values.map(singleToken => `${singleToken.tokenAddress}_${singleToken.chainId}`);
            filteredVaults = filteredVaults.filter(
              vault => vault.assets.find(asset => filter.includes(`${asset.contract_address}_${vault.chain.id}`)) !== undefined
            ) as SupSingleVaultV3[];
          }
          break;
        case "superpools":
          // eslint-disable-next-line no-case-declarations
          const displayOnlySuperpools = (singleFilter.values as boolean) ?? false;
          if (displayOnlySuperpools) {
            filteredVaults = filteredVaults.filter(vault => vault.super_pools?.length ?? 0 > 0) as SupSingleVaultV3[];
          }
          break;
      }
    });

    if (processAuthenticatedFilter) {
      const displayUnauthenticated = (storedData.filters.find(storedFilter => storedFilter.name === "unauthenticated")?.values ?? false) as boolean;

      if (!displayUnauthenticated) {
        filteredVaults = filteredVaults.filter(vault => isAuthenticated(vault));
      }
    }

    if (allowFavoritesFilter && storedData.favorites.length > 0 && storedData.filterFavorites) {
      filteredVaults = filteredVaults.filter(vault => storedData.favorites.includes(vault.slug));
    }

    if (processVisibility) {
      filteredVaults = filteredVaults.filter(vault => !["hidden"].includes(vault.visibility));
    }

    return filteredVaults.sort((a, b) => getVaultApy(b) - getVaultApy(a));
  }, [rawVaults, storedData.filters, storedData.favorites, storedData.filterFavorites, processAuthenticatedFilter, allowFavoritesFilter, processVisibility, rewardSettings]);

  const isFiltered = useMemo(() => {
    return rawVaults.length > 0 && vaults.length !== rawVaults.length;
  }, [vaults, rawVaults]);

  return {
    vaults,
    isFiltered,
    isLoading,
    isError,
    isCompleted,
  };
}
